import { forwardRef } from "react";
import CheckIcon from "@svg/new-design/check";
import CloseRemoveIcon from "@svg/new-design/close-remove";
import IconDangerCircle from "@svg/new-design/danger-circle";
import DangerTriangleIcon from "@svg/new-design/danger-triangle";
import Button from "../button/Button";
import { BUTTON_SIZE } from "@shared/components/button";
import { useInput } from ".";
import { SharedInputProps, INPUT_SIZE } from "./types";
import { twMerge } from "tailwind-merge";
import classNames from "classnames";

const Input = forwardRef<HTMLInputElement, SharedInputProps>((props, ref) => {
  const {
    title,
    name,
    size = INPUT_SIZE.LARGE,
    className,
    value,
    error,
    inputClassName,
    showErrorIcon,
    hintMessage,
    required = true,
    startIcon,
    endIcon,
    successMessage,
    showSuccessIcon,
    startIconClassName,
    endIconClassName,
    endIconAction,
    startIconAction,
    onBlur,
    onFocus,
    showInfoIcon,
    disabled = false,
    clearHandler,
    parentClassName,
    button,
    labelClassName,
    ...rest
  } = props;

  const StartIcon = startIcon;
  const EndIcon = endIcon;

  const {
    setHasFocus,
    labelClass,
    containerClass,
    inputClass,
    startIconClass,
    endIconClass,
    clearIconClass,
    errorContainerClass,
    errorIconClass,
    errorTextClass,
    successTextClass,
    hintTextClass,
  } = useInput({
    size,
    title,
    error,
    successMessage,
    disabled,
    parentClassName,
    startIcon,
    startIconClassName,
    endIconClassName,
    inputClassName,
    extraLabelClassName: labelClassName,
  });

  return (
    <div className={twMerge("group  justify-between relative", className)}>
      {title && (
        <label
          className={classNames(labelClass, labelClassName)}
          htmlFor={name}
        >
          {required && (
            <span className="text-fds-danger text-sm pl-1.5">*</span>
          )}
          {title}
        </label>
      )}

      <div className={containerClass}>
        {StartIcon && (
          <StartIcon className={startIconClass} onClick={startIconAction} />
        )}

        <input
          ref={ref}
          {...rest}
          value={value}
          onFocus={(e) => {
            setHasFocus(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setHasFocus(false);
            onBlur?.(e);
          }}
          className={twMerge(inputClass, !value && "select-none")}
          disabled={disabled}
          name={name}
        />

        {EndIcon && (
          <EndIcon className={endIconClass} onClick={endIconAction} />
        )}

        {button?.show && (
          <Button
            disabled={button.disabled}
            color={button.color}
            size={BUTTON_SIZE.MEDIUM}
            variant={button.variant}
            onClick={() => button.action?.(value as string)}
            className={twMerge(
              "absolute left-1",
              button?.action
                ? "cursor-pointer"
                : "cursor-auto active:bg-fds-success-light hover:bg-fds-success-light",
              button.className
            )}
          >
            {button.text}
          </Button>
        )}

        {Boolean(String(value || "").length && clearHandler) && (
          <div
            className="absolute cursor-pointer text-fds-icon-secondary left-2.5"
            onClick={clearHandler}
          >
            <CloseRemoveIcon className={clearIconClass} />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        {error && (
          <div className={errorContainerClass}>
            {showErrorIcon && <DangerTriangleIcon className={errorIconClass} />}
            <span className={errorTextClass}>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="flex self-start">
            {showSuccessIcon && <CheckIcon />}
            <span className={successTextClass}>{successMessage}</span>
          </div>
        )}

        {hintMessage && !error && !successMessage && (
          <div className="flex gap-1 items-center">
            {showInfoIcon && (
              <IconDangerCircle className="w-4 h-4 text-fds-icon-secondary" />
            )}
            <span className={hintTextClass}>{hintMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
});

Input.displayName = "Input";

export default Input;
